/** 認証済みの API クライアントを配る。 */
import { useEffect, useMemo, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { signOut } from "./auth";
import { createApi } from "./api";
import { ApiContext } from "./apiContext";
import type { RuntimeConfig } from "./config";

export function ApiProvider({
  config,
  children,
}: {
  config: RuntimeConfig;
  children: ReactNode;
}) {
  const auth = useAuth();
  useEffect(() => {
    let leaving = false;
    const unauthorized = () => { if (!leaving) { leaving = true; void signOut(auth); } };
    window.addEventListener("itmgmt-unauthorized", unauthorized);
    return () => window.removeEventListener("itmgmt-unauthorized", unauthorized);
  }, [auth]);
  const qc = useQueryClient();
  useEffect(() => {
    const clear = () => { void qc.cancelQueries({ queryKey: ["dlp", "mail"] }); qc.removeQueries({ queryKey: ["dlp", "mail"] }); };
    auth.events.addUserUnloaded(clear);
    auth.events.addUserSignedOut(clear);
    return () => { auth.events.removeUserUnloaded(clear); auth.events.removeUserSignedOut(clear); clear(); };
  }, [auth.events, auth.user?.access_token, qc]);
  const api = useMemo(
    () => createApi(config.apiBase, () => auth.user?.access_token),
    [config.apiBase, auth.user?.access_token],
  );
  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
}
